import React, { useState } from "react";
import { FaUser, FaEnvelope, FaPhone, FaClipboardList, FaComments, FaCheckCircle, FaStar, FaMapMarkerAlt, FaLock } from 'react-icons/fa';
import "../Styles/Consultation.css";
import "../Styles/Animations.css";
import { Helmet } from 'react-helmet';
import Toast from './Toast';
import Reveal, { RevealGroup, RevealItem } from './Reveal';
import MagneticButton from './MagneticButton';

export default function Consultation() {

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    inquiryType: "",
    description: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [toast, setToast] = useState(null);

  const consultationForm = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || "https://veabroad.com"}/api/consultationForm/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...formData, category: "Consultation", source: "Consultation page" }),
      });
      const result = await response.json();
      if (result.status === "success") {
        setIsSubmitted(true);
        setToast({ message: 'Consultation request submitted successfully!', type: 'success' });
        setTimeout(() => {
          setFormData({
            name: "",
            email: "",
            phone: "",
            inquiryType: "",
            description: "",
          });
          setIsSubmitted(false);
          setToast(null);
        }, 3000);
      }
    } catch (error) {
      setToast({ message: 'Error submitting form. Please try again.', type: 'error' });
      setTimeout(() => setToast(null), 3000);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="consultation-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <Helmet>
        <title>Book a Free Consultation | Expert Study & Migration Advice</title>
        <meta name="description" content="Schedule your free, zero-obligation assessment with certified study abroad and immigration advisors. Get answers within 24 hours. Start today!" />
        <link rel="canonical" href="https://veabroad.com/consultationForm" />
        <meta property="og:title" content="Book a Free Consultation | Expert Study & Migration Advice" />
        <meta property="og:description" content="Schedule your free, zero-obligation assessment with certified study abroad and immigration advisors. Get answers within 24 hours. Start today!" />
        <meta property="og:url" content="https://veabroad.com/consultationForm" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://veabroad.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Book a Free Consultation | Expert Study & Migration Advice" />
        <meta name="twitter:description" content="Schedule your free, zero-obligation assessment with certified study abroad and immigration advisors. Get answers within 24 hours. Start today!" />
        <meta name="twitter:image" content="https://veabroad.com/og-image.jpg" />
      </Helmet>
      <div className="va-blob-field" aria-hidden="true">
        <div className="va-blob va-blob-mint" style={{ width: "420px", height: "420px", top: "-80px", right: "-100px" }}></div>
        <div className="va-blob va-blob-teal" style={{ width: "360px", height: "360px", bottom: "-60px", left: "-90px" }}></div>
      </div>

      <div className="consultation-container">
        {/* HEADING - always first */}
        <Reveal as="div" direction="up" className="consultation-heading">
          <h1>Start Your <span className="va-shine-text va-shine-text-on-dark">Global Journey</span> Today</h1>
          <p className="info-description text-white">
            Over 1,000 families have trusted us with their immigration, study abroad, and visa goals. Our certified consultants are ready to guide you — for free.
          </p>
        </Reveal>

        <div className="consultation-content">
          {/* FORM - second */}
          <Reveal as="div" direction="up" delay={0.1} className="consultation-form-wrapper">
            {isSubmitted ? (
              <div className="success-message">
                <FaCheckCircle className="success-icon" />
                <h2>Thank You!</h2>
                <p>Your consultation request has been submitted successfully. Our team will contact you shortly.</p>
              </div>
            ) : (
              <form className="consultation-form" onSubmit={consultationForm}>
                <h2>Book Your Free Consultation</h2>
                <p className="consultation-form-subtitle">Fill in your details and we'll get back to you within 24 hours.</p>
                <div className="form-group">
                  <label><FaUser /> Full Name</label>
                  <input type="text" name="name" placeholder="Enter your full name" value={formData.name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label><FaEnvelope /> Email Address</label>
                  <input type="email" name="email" placeholder="your.email@example.com" value={formData.email} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label><FaPhone /> Phone Number</label>
                  <input type="tel" name="phone" placeholder="+91 (555) 000-0000" value={formData.phone} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label><FaClipboardList /> Service Type</label>
                  <select name="inquiryType" value={formData.inquiryType} onChange={handleChange} required>
                    <option value="">Select a service</option>
                    <option value="Immigration">Immigration</option>
                    <option value="Work abroad">Work Abroad</option>
                    <option value="Study Overseas">Study Overseas</option>
                    <option value="Visa services">Visa Services</option>
                    <option value="Permanent Residency">Permanent Residency</option>
                    <option value="IELTS Coachings">IELTS Coaching</option>
                  </select>
                </div>
                <div className="form-group">
                  <label><FaComments /> Tell Us More</label>
                  <textarea name="description" placeholder="Share your goals, concerns, or any specific questions..." value={formData.description} onChange={handleChange} rows="4" required />
                </div>
                <MagneticButton as="span" style={{ display: "flex", width: "100%" }}>
                  <button type="submit" className="submit-btn va-shimmer-btn" style={{ width: "100%" }}>Submit Consultation Request</button>
                </MagneticButton>
              </form>
            )}
          </Reveal>

          {/* FEATURES - third */}
          <Reveal as="div" direction="right" delay={0.15} className="consultation-info">
            <RevealGroup as="div" className="info-features" stagger={0.08}>
              <RevealItem direction="up" className="feature-item va-hover-lift">
                <FaStar className="feature-icon" />
                <div><h3>Expert Consultants</h3><p>Certified advisors with years of immigration and study abroad experience</p></div>
              </RevealItem>
              <RevealItem direction="up" className="feature-item va-hover-lift">
                <FaMapMarkerAlt className="feature-icon" />
                <div><h3>50+ Countries Covered</h3><p>We guide you to the right destination based on your profile and goals</p></div>
              </RevealItem>
              <RevealItem direction="up" className="feature-item va-hover-lift">
                <FaLock className="feature-icon" />
                <div><h3>Transparent & Free</h3><p>No hidden charges. Your first consultation is completely free</p></div>
              </RevealItem>
              <RevealItem direction="up" className="feature-item va-hover-lift">
                <FaPhone className="feature-icon" />
                <div><h3>24-Hour Response</h3><p>Our team gets back to every inquiry within 24 hours, guaranteed</p></div>
              </RevealItem>
              <RevealItem direction="up" className="feature-item va-hover-lift">
                <FaClipboardList className="feature-icon" />
                <div><h3>End-to-End Support</h3><p>From profile evaluation to post-landing assistance, we handle it all</p></div>
              </RevealItem>
            </RevealGroup>
            <div className="consultation-stats">
              <div className="consultation-stat va-pop">
                <span className="consultation-stat-num">1000+</span>
                <span className="consultation-stat-label">Students Helped</span>
              </div>
              <div className="consultation-stat va-pop">
                <span className="consultation-stat-num">98%</span>
                <span className="consultation-stat-label">Success Rate</span>
              </div>
              <div className="consultation-stat va-pop">
                <span className="consultation-stat-num">24hr</span>
                <span className="consultation-stat-label">Response Time</span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}